import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  CheckCircle2,
  Download,
  Loader2,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyButton } from '@/components/CopyButton';
import { formatMicros } from '@/components/MoneyAmount';
import { ApiError } from '@/lib/fetcher';
import {
  type BatonMetaData,
  type VerifyChainResult,
  batonMetaKey,
  batonProofKey,
  challengeBaton,
  fetchBatonProof,
  verifyBatonChain,
  witnessBaton,
} from '@/lib/queries';

// Brief 129 — the witness sub-object from /meta, threaded into the
// confirm dialog so it shows definite charge copy.
type WitnessMetaInfo = NonNullable<BatonMetaData['flagsSnapshot']>['witness'];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export function TrustTab({ id, meta }: { id: string; meta?: BatonMetaData }) {
  if (!meta) {
    return <Skeleton className="h-64 w-full" />;
  }

  const isWitnessed = meta.trustState === 'witnessed';
  const isActive = meta.deleteState === 'active';
  const isChained = meta.hashChaining;
  const chainAnchored = meta.chainAnchorHash !== null;

  return (
    <div className="space-y-6">
      {/* 1 — trust state (always) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Trust state
          </CardTitle>
          <CardDescription>
            Integrity verification for this baton.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="State">
              <Badge variant={isWitnessed ? 'success' : 'secondary'}>
                {meta.trustState ?? 'open'}
              </Badge>
            </Field>
            <Field label="Hash chaining">
              {isChained ? 'enabled' : 'not enabled'}
            </Field>
            {isWitnessed && meta.notarizedAt ? (
              <Field label="Witnessed">
                {format(parseISO(meta.notarizedAt), 'MMM d, yyyy HH:mm')}
              </Field>
            ) : null}
            {isChained && meta.chainAnchorHash ? (
              <Field label="Chain head">
                <span className="flex items-center gap-1">
                  <code className="font-mono text-xs">
                    {meta.chainAnchorHash.slice(0, 14)}…
                  </code>
                  <CopyButton value={meta.chainAnchorHash} size="sm" />
                </span>
              </Field>
            ) : null}
          </dl>
          <p className="mt-4 text-sm text-muted-foreground">
            {isWitnessed
              ? 'This baton has been document-witnessed: its content is frozen and signed by the platform witness key. Use the proof and challenge below to verify it.'
              : isChained
                ? 'This baton is hash-chained — each entry links to the previous, so tampering is detectable. Verify the chain below, and document-witness it to freeze and sign it.'
                : 'This baton is not hash-chained. You can still document-witness it to freeze and sign its current content.'}
          </p>
        </CardContent>
      </Card>

      {/* 2 — verify chain (chained batons only) */}
      {isChained ? <VerifyChainCard id={id} /> : null}

      {/* 3 — document-witness action / state */}
      <WitnessCard
        id={id}
        isWitnessed={isWitnessed}
        isActive={isActive}
        isChained={isChained}
        chainAnchored={chainAnchored}
        deleteState={meta.deleteState}
        notarizedAt={meta.notarizedAt}
        witness={meta.flagsSnapshot?.witness}
      />

      {/* 4 + 5 — proof + challenge (witnessed only) */}
      {isWitnessed ? (
        <>
          <ProofCard id={id} />
          <ChallengeCard id={id} />
        </>
      ) : null}
    </div>
  );
}

function VerifyChainCard({ id }: { id: string }) {
  const [result, setResult] = React.useState<VerifyChainResult | null>(null);
  const m = useMutation({
    mutationFn: () => verifyBatonChain(id),
    onSuccess: (r) => setResult(r),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Verify chain</CardTitle>
        <CardDescription>
          Walks the hash links from the stored hash columns — free, no content
          read.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={() => m.mutate()} disabled={m.isPending} variant="outline">
          {m.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
            </>
          ) : (
            'Verify chain'
          )}
        </Button>
        {m.error ? (
          <Alert variant="destructive">
            <AlertTitle>
              {m.error instanceof ApiError ? m.error.code : 'Failed'}
            </AlertTitle>
            <AlertDescription>
              {m.error instanceof ApiError ? m.error.message : 'Try again.'}
            </AlertDescription>
          </Alert>
        ) : result ? (
          result.intact ? (
            <Alert>
              <AlertTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-4 w-4" /> Chain intact
              </AlertTitle>
              <AlertDescription>
                All links verify.{' '}
                {result.head ? (
                  <span className="font-mono text-xs">
                    head {result.head.slice(0, 14)}…
                  </span>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <AlertTitle className="flex items-center gap-2">
                <XCircle className="h-4 w-4" /> Chain broken
              </AlertTitle>
              <AlertDescription>
                Broken at entry {result.brokenAt}: {result.reason}
              </AlertDescription>
            </Alert>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

function WitnessCard({
  id,
  isWitnessed,
  isActive,
  isChained,
  chainAnchored,
  deleteState,
  notarizedAt,
  witness,
}: {
  id: string;
  isWitnessed: boolean;
  isActive: boolean;
  isChained: boolean;
  chainAnchored: boolean;
  deleteState: string;
  notarizedAt: string | null;
  witness?: WitnessMetaInfo;
}) {
  // State-based availability (no create-time "witnessable" flag exists).
  const disabledReason = !isActive
    ? `This baton is ${deleteState} — only an active baton can be witnessed.`
    : isChained && !chainAnchored
      ? 'This chained baton has no entries yet — add content first; the hash chain initializes on the first write.'
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Document-witness</CardTitle>
        <CardDescription>
          Freeze the baton's current content and sign it with the platform
          witness key. One-time, irreversible.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isWitnessed ? (
          <Alert>
            <AlertTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-4 w-4" /> Witnessed
            </AlertTitle>
            <AlertDescription>
              {notarizedAt
                ? `Signed ${format(parseISO(notarizedAt), 'MMM d, yyyy HH:mm')}. `
                : ''}
              The signed proof and a candidate challenge are below.
            </AlertDescription>
          </Alert>
        ) : disabledReason ? (
          <div className="space-y-2">
            <Button disabled>Witness this baton</Button>
            <p className="text-sm text-muted-foreground">{disabledReason}</p>
          </div>
        ) : (
          <WitnessDialog id={id} witness={witness} />
        )}
      </CardContent>
    </Card>
  );
}

function WitnessDialog({ id, witness }: { id: string; witness?: WitnessMetaInfo }) {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const m = useMutation({
    mutationFn: () => witnessBaton(id),
    onSuccess: () => {
      // Flips trustState → witnessed; the tab re-renders with proof +
      // challenge, and Overview's trust state updates.
      void qc.invalidateQueries({ queryKey: batonMetaKey(id) });
    },
  });

  const close = (next: boolean) => {
    setOpen(next);
    if (!next) m.reset();
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogTrigger asChild>
        <Button>Witness this baton</Button>
      </DialogTrigger>
      <DialogContent>
        {m.isSuccess ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" /> Witnessed
              </DialogTitle>
              <DialogDescription>
                The baton is frozen and signed. The proof and challenge are now
                available on the Trust tab.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => close(false)}>Done</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Witness this baton?</DialogTitle>
              <DialogDescription>
                This freezes the baton's current content and signs it. It can't
                be undone.
              </DialogDescription>
            </DialogHeader>
            <Alert>
              <AlertTitle>Charge</AlertTitle>
              <AlertDescription>
                {witness?.prepaid
                  ? 'This baton was prepaid at create — no charge to witness.'
                  : `You will be charged ${formatMicros(
                      witness?.pricePerFireMicros ?? 50_000,
                      { minorDigits: 2 },
                    )} to witness this baton.`}
              </AlertDescription>
            </Alert>
            {m.error ? (
              <Alert variant="destructive">
                <AlertTitle>
                  {m.error instanceof ApiError ? m.error.code : 'Failed'}
                </AlertTitle>
                <AlertDescription>
                  {m.error instanceof ApiError ? m.error.message : 'Try again.'}
                </AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => close(false)}
                disabled={m.isPending}
              >
                Cancel
              </Button>
              <Button onClick={() => m.mutate()} disabled={m.isPending}>
                {m.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Witnessing…
                  </>
                ) : (
                  'Confirm & witness'
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProofCard({ id }: { id: string }) {
  const q = useQuery({
    queryKey: batonProofKey(id),
    queryFn: () => fetchBatonProof(id),
    retry: false,
  });
  const json = q.data ? JSON.stringify(q.data, null, 2) : '';
  const downloadHref = q.data
    ? `data:application/json;charset=utf-8,${encodeURIComponent(json)}`
    : undefined;

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>Signed proof</CardTitle>
          <CardDescription>
            The document-witness attestation — hand it to an offline verifier.
          </CardDescription>
        </div>
        {q.data ? (
          <div className="flex gap-2">
            <CopyButton value={json} label="Copy" variant="outline" size="sm" />
            <Button asChild variant="outline" size="sm">
              <a href={downloadHref} download={`baton-${id}-proof.json`}>
                <Download className="h-4 w-4" /> JSON
              </a>
            </Button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        {q.isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : q.error ? (
          <Alert variant="destructive">
            <AlertTitle>
              Couldn't load proof (
              {q.error instanceof ApiError ? q.error.code : 'unknown'})
            </AlertTitle>
          </Alert>
        ) : q.data ? (
          <pre className="max-h-96 overflow-auto rounded-md border bg-muted/40 p-3 text-xs leading-5">
            {json}
          </pre>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ChallengeCard({ id }: { id: string }) {
  const [field, setField] = React.useState<'content' | 'contentHash'>('content');
  const [value, setValue] = React.useState('');
  const m = useMutation({
    mutationFn: () =>
      challengeBaton(
        id,
        field === 'content'
          ? { content: value }
          : { contentHash: value.trim() },
      ),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Challenge</CardTitle>
        <CardDescription>
          Check whether a candidate matches what was witnessed — paste the
          content, or a precomputed <code className="text-xs">0x…</code>{' '}
          SHA-256 hash. Free.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <Label htmlFor="chal-field" className="text-muted-foreground">
            Candidate
          </Label>
          <select
            id="chal-field"
            value={field}
            onChange={(e) => {
              setField(e.target.value as 'content' | 'contentHash');
              setValue('');
              m.reset();
            }}
            className="rounded border border-input bg-background px-2 py-1"
          >
            <option value="content">Content</option>
            <option value="contentHash">SHA-256 hash</option>
          </select>
        </div>
        {field === 'content' ? (
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            rows={4}
            placeholder="Paste the candidate content…"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
          />
        ) : (
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="0x + 64 hex"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
          />
        )}
        {m.error ? (
          <Alert variant="destructive">
            <AlertTitle>
              {m.error instanceof ApiError ? m.error.code : 'Failed'}
            </AlertTitle>
            <AlertDescription>
              {m.error instanceof ApiError ? m.error.message : 'Try again.'}
            </AlertDescription>
          </Alert>
        ) : m.data ? (
          m.data.match ? (
            <Alert>
              <AlertTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-4 w-4" /> Match
              </AlertTitle>
              <AlertDescription>
                This candidate matches the witnessed content.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <AlertTitle className="flex items-center gap-2">
                <XCircle className="h-4 w-4" /> No match
              </AlertTitle>
              <AlertDescription>
                This candidate does not match the witnessed content.
              </AlertDescription>
            </Alert>
          )
        ) : null}
        <Button
          onClick={() => m.mutate()}
          disabled={!value.trim() || m.isPending}
          variant="outline"
        >
          {m.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Checking…
            </>
          ) : (
            'Run challenge'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
